import { defineStore } from 'pinia'
import * as uuid from 'uuid'
import omit from 'just-omit'
import pick from 'just-pick'
import { useSocketIOStore } from './socket-io'
import { NOTIFICATIONS_TYPES } from '@shared/constants'
import { useMessagesStore } from './messages'

export const UNFINISHED_EXPORT_TIMEOUT_MS = 15 * 60 * 1000
// A result this late is not expected anymore, the timed out notification stays.
export const ABANDONED_EXPORT_RETENTION_MS = 24 * 60 * 60 * 1000
export const TENANT_SWITCH_TIMEOUT_REASON = 'tenantSwitch'

const AWAITING_TYPES = [
  NOTIFICATIONS_TYPES.LOADING,
  NOTIFICATIONS_TYPES.PROGRESS
]

let expiryTimer = null

export const useExportsStore = defineStore('exports', {
  persist: true,
  state: () => ({
    exports: {},
    // Last preparing/progress/uploading event per export, the timeout counts from it
    lastActivityAt: {}
  }),
  actions: {
    init(vm) {
      const socketIoStore = useSocketIOStore()
      const socket = socketIoStore.socket
      const parseSocketData = socketIoStore.getSocketDataParser()
      const moduleName = this.$id

      socket?.on('exportPreparing', (data) => {
        const { isValidId, id, originKey, storedItem } = parseSocketData(
          data,
          this.exports
        )
        if (!isValidId) return
        vm?.$log?.debug('Exports | exportPreparing')
        this.touchExport(id)

        const message = {
          id,
          read: false,
          operation: {
            module: moduleName,
            originKey,
            type: NOTIFICATIONS_TYPES.LOADING,
            ...storedItem
          }
        }
        const messagesStore = useMessagesStore()
        messagesStore.updateMessage({ id, changedProp: message }, true)
      })

      socket?.on('exportProgress', (data) => {
        const { isValidId, id, originKey, parsedData, storedItem } =
          parseSocketData(data, this.exports)
        if (!isValidId) return
        vm?.$log?.debug('Exports | exportProgress')
        this.touchExport(id)

        const message = {
          read: false,
          operation: {
            originKey,
            module: moduleName,
            progress: parsedData.progress,
            type: NOTIFICATIONS_TYPES.PROGRESS,
            ...storedItem
          }
        }
        const messagesStore = useMessagesStore()
        messagesStore.updateMessage({ id, changedProp: message }, true)
      })

      socket?.on('exportSuccess', (data) => {
        const { isValidId, id, originKey, parsedData, storedItem } =
          parseSocketData(data, this.exports)
        if (!isValidId) return
        vm?.$log?.debug('Exports | exportSuccess')

        // We add the succeeded state to true, so in case of unordered messages, we don't change the state
        const message = {
          read: false,
          succeeded: true,
          operation: {
            originKey,
            module: moduleName,
            url: parsedData.url,
            filename: parsedData.filename,
            expiresAt: parsedData.expires_at,
            type: NOTIFICATIONS_TYPES.SUCCESS,
            ...storedItem
          }
        }
        const messagesStore = useMessagesStore()
        messagesStore.updateMessage({ id, changedProp: message })
        this.forgetExport(id)
      })

      socket?.on('exportError', (data) => {
        const { isValidId, id, originKey, storedItem } = parseSocketData(
          data,
          this.exports
        )
        if (!isValidId) return
        vm?.$log?.debug('Exports | exportError')

        const message = {
          read: false,
          operation: {
            originKey,
            module: moduleName,
            type: NOTIFICATIONS_TYPES.ERROR,
            ...storedItem
          }
        }
        const messagesStore = useMessagesStore()
        messagesStore.updateMessage({ id, changedProp: message })
        this.forgetExport(id)
      })

      socket?.on('exportEmpty', (data) => {
        const { isValidId, id, originKey, storedItem } = parseSocketData(
          data,
          this.exports
        )
        if (!isValidId) return
        vm?.$log?.debug('Exports | exportEmpty')

        const message = {
          read: false,
          operation: {
            originKey,
            module: moduleName,
            type: NOTIFICATIONS_TYPES.EMPTY,
            ...storedItem
          }
        }
        const messagesStore = useMessagesStore()
        messagesStore.updateMessage({ id, changedProp: message })
        this.forgetExport(id)
      })

      socket?.on('exportUploading', (data) => {
        const { isValidId, id } = parseSocketData(data, this.exports)
        if (!isValidId) return
        vm?.$log?.debug('Exports | exportUploading')
        this.touchExport(id)
      })
    },
    setNewExport({ exportId, payload }) {
      const messagesStore = useMessagesStore()
      const message = {
        id: exportId,
        read: false,
        operation: {
          module: this.$id,
          type: NOTIFICATIONS_TYPES.LOADING,
          ...payload
        }
      }
      this.exports = { ...this.exports, [exportId]: payload }
      messagesStore.setLocalMessage(message)
      useSocketIOStore().setActiveId(exportId)
      this.expireUnfinishedExports()
    },
    touchExport(id, now = Date.now()) {
      if (!this.exports[id]) return
      this.lastActivityAt = { ...this.lastActivityAt, [id]: now }
    },
    forgetExport(id) {
      this.exports = omit(this.exports, id)
      this.lastActivityAt = omit(this.lastActivityAt, id)
      useSocketIOStore().removeActiveId(id)
    },
    // The new dashboard's socket never reports exports started in the previous one
    timeOutExportsOnTenantSwitch() {
      this.expireUnfinishedExports(Infinity, TENANT_SWITCH_TIMEOUT_REASON)
    },
    expireUnfinishedExports(now = Date.now(), timeoutReason = undefined) {
      const messagesStore = useMessagesStore()
      let nextCheckIn = Infinity

      for (const [id, payload] of Object.entries(this.exports)) {
        const message = messagesStore.messages[id]
        const startedAt = new Date(payload?.date).getTime()
        if (!AWAITING_TYPES.includes(message?.operation?.type)) continue
        if (!Number.isFinite(startedAt)) continue

        const lastSeenAt = Math.max(startedAt, this.lastActivityAt[id] ?? 0)
        const remaining = lastSeenAt + UNFINISHED_EXPORT_TIMEOUT_MS - now
        if (remaining > 0) {
          nextCheckIn = Math.min(nextCheckIn, remaining)
          continue
        }

        messagesStore.updateMessage({
          id,
          changedProp: {
            read: false,
            operation: {
              ...message.operation,
              type: NOTIFICATIONS_TYPES.TIMEOUT,
              timeoutReason
            }
          }
        })
      }

      clearTimeout(expiryTimer)
      expiryTimer = Number.isFinite(nextCheckIn)
        ? setTimeout(() => this.expireUnfinishedExports(), nextCheckIn)
        : null
    },
    // After a page reload only exports can be resumed: their payload is persisted,
    // while the imports and assets stores start empty.
    restorePendingExports(now = Date.now()) {
      this.pruneExports(Object.keys(useMessagesStore().messages))
      this.expireUnfinishedExports(now)
      this.forgetAbandonedExports(now)
      useSocketIOStore().pruneActiveIds(Object.keys(this.exports))
    },
    forgetAbandonedExports(now = Date.now()) {
      for (const [id, payload] of Object.entries(this.exports)) {
        const startedAt = new Date(payload?.date).getTime()
        if (startedAt + ABANDONED_EXPORT_RETENTION_MS <= now) {
          this.forgetExport(id)
        }
      }
    },
    pruneExports(knownIds) {
      this.exports = pick(this.exports, knownIds)
      this.lastActivityAt = pick(this.lastActivityAt, knownIds)
    },
    setFinishedExport({ exportResult, payload }) {
      const messagesStore = useMessagesStore()
      const message = {
        id: uuid.v4(),
        read: false,
        operation: {
          module: this.$id,
          url: exportResult.url,
          filename: exportResult.filename,
          expiresAt: exportResult.expires_at,
          type: NOTIFICATIONS_TYPES.SUCCESS,
          ...payload
        }
      }
      messagesStore.setLocalMessage(message)
    }
  }
})
